<template>
  <button
    :disabled="disabled"
    :style="buttonStyle"
    :class="buttonClasses"
    @mousedown="handleMouseDown"
    @mouseup="handleMouseUp"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @touchstart="handleTouchStart"
    @touchend="handleTouchEnd"
    @click="$emit('click', $event)"
  >
    <span class="relative overflow-hidden w-full h-full flex items-center justify-center">
      <span :class="contentSpanClasses" :style="contentSpanStyle">
        <slot />
      </span>
      <span :class="duplicateSpanClasses" :style="duplicateSpanStyle">
        <slot />
      </span>
    </span>
  </button>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'

type ButtonVariant = 'validation' | 'cancel' | 'extra-validation'

interface Props {
  variant?: ButtonVariant
  expandWidth?: boolean
  className?: string
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'validation',
  expandWidth: false,
  className: '',
  disabled: false
})

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

const height = ref(50)
const width = ref<number | string>(120)
const pressed = ref(false)
const isHovered = ref(false)
const isMobile = ref(false)

const updateSize = () => {
  const screenWidth = window.innerWidth
  
  const WidthMobileM = 120
  const ScreenWidthMobileM = 320
  const WidthKscreen = 233
  const ScreenWidthKscreen = 2560
  const a = (WidthKscreen - WidthMobileM) / (ScreenWidthKscreen - ScreenWidthMobileM)
  const b = WidthMobileM - a * ScreenWidthMobileM
  const widthRef = a * screenWidth + b
  const calculatedHeight = 0.30 * widthRef
  
  if (props.expandWidth) {
    width.value = 'auto'
    height.value = calculatedHeight
  } else {
    width.value = widthRef
    height.value = calculatedHeight
  }
  isMobile.value = screenWidth < 1024
}

onMounted(() => {
  updateSize()
  window.addEventListener('resize', updateSize)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateSize)
})

const radiusRatio = 15 / 66
const borderRadius = computed(() => height.value * radiusRatio)

const variantStyles = computed(() => {
  switch (props.variant) {
    case 'validation':
      return {
        bgClass: 'bg-[#04406E]',
        textClass: 'text-white',
      }
    case 'extra-validation':
      return {
        bgClass: 'bg-[#07A766]',
        textClass: 'text-white',
        borderClass: 'border-2 border-white'
      }
    case 'cancel':
      return {
        bgClass: 'bg-transparent',
        textClass: 'text-white',
        borderClass: 'border-2 border-white'
      }
    default:
      return {
        bgClass: 'bg-[#04406E]',
        textClass: 'text-white',
      }
  }
})

const getScale = computed(() => {
  if (pressed.value) return 'scale(0.90)'
  if (isHovered.value && !isMobile.value) return 'scale(1.05)'
  return 'scale(1)'
})

const handleMouseDown = () => {
  pressed.value = true
}

const handleMouseUp = () => {
  pressed.value = false
}

const handleMouseEnter = () => {
  isHovered.value = true
}

const handleMouseLeave = () => {
  pressed.value = false
  isHovered.value = false
}

const handleTouchStart = () => {
  if (!isMobile.value) return
  pressed.value = true
}

const handleTouchEnd = () => {
  if (!isMobile.value) return
  pressed.value = false
}

const buttonStyle = computed(() => {
  const baseStyle: Record<string, string | number> = {
    width: props.expandWidth ? 'auto' : typeof width.value === 'number' ? `${width.value}px` : width.value,
    height: `${height.value}px`,
    borderRadius: `${borderRadius.value}px`,
    transform: getScale.value,
    transition: 'transform 0.3s ease-out, border-radius 0.2s ease, opacity 0.2s ease',
    overflow: 'hidden',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',
    userSelect: 'none',
    WebkitTouchCallout: 'none',
    WebkitTapHighlightColor: 'transparent',
    minWidth: props.expandWidth ? '120px' : 'auto',
    whiteSpace: props.expandWidth ? 'nowrap' : 'normal',
    opacity: props.disabled ? 0.5 : 1,
    cursor: props.disabled ? 'not-allowed' : 'pointer'
  }
  
  return baseStyle
})

const buttonClasses = computed(() => {
  const classes = [
    variantStyles.value.bgClass,
    variantStyles.value.textClass,
    variantStyles.value.borderClass,
    props.expandWidth ? 'px-4' : 'px-7',
    'font-bold',
    'text-lg',
    'group',
    'relative',
    'cursor-pointer',
    props.className
  ].filter(Boolean)
  return classes.join(' ')
})

const contentSpanClasses = computed(() => {
  return [
    'flex items-center justify-center transition-transform duration-300 w-full h-full',
    !isMobile.value ? 'group-hover:translate-x-full' : ''
  ].join(' ')
})

const contentSpanStyle = computed(() => {
  if (isMobile.value) {
    return {
      transform: pressed.value ? 'translateX(100%)' : 'translateX(0)'
    }
  }
  return {}
})

const duplicateSpanClasses = computed(() => {
  return [
    'flex items-center justify-center absolute top-0 left-0 w-full h-full transition-transform duration-300',
    !isMobile.value ? 'group-hover:translate-x-[100%]' : ''
  ].join(' ')
})

const duplicateSpanStyle = computed(() => {
  const baseStyle = { left: '-100%' }
  if (isMobile.value) {
    return {
      ...baseStyle,
      transform: pressed.value ? 'translateX(100%)' : 'translateX(0)'
    }
  }
  return baseStyle
})
</script>

<style scoped>
button {
  cursor: pointer;
}
</style>